import { ApiError } from '@planetra/api-client';

/**
 * Turning anything a request can throw into a closed set the UI branches on
 * (P0-WEB-04).
 *
 * Call sites branch on `kind`, never on a status number. That is the whole
 * point: PRD §15 requires a designed response per scenario, and a component
 * that reads `error.status === 403` has quietly taken on the job of knowing
 * that 403 and a 401 with a revoked session are different problems for the
 * user. One classifier gets that right once.
 *
 * Pure, and free of React and i18n. `use-api-error.ts` turns a failure into
 * words; this file decides only what happened.
 */

interface FailureBase {
  /** HTTP status, absent when the request never reached the server. */
  readonly status: number | undefined;
  /**
   * The stable machine-readable code from the body's `error` field, where the
   * handler provided one (the `Error` schema in docs/api/openapi.json).
   */
  readonly code: string | undefined;
  /**
   * The API's own `message`.
   *
   * For the error reporter and support references — **never rendered to a
   * user**. Laravel resolves `__()` against the application locale, and there
   * is no locale negotiation middleware in apps/api, so these arrive in English
   * whatever the user has chosen. The one exception is `fieldErrors` below,
   * which is discussed there.
   */
  readonly serverMessage: string | undefined;
}

export type ApiFailure =
  /** The caller aborted. Not a failure — callers discard it silently. */
  | (FailureBase & { readonly kind: 'cancelled' })
  /** No response at all: DNS, connection refused, CORS, or a dropped network. */
  | (FailureBase & { readonly kind: 'offline' })
  /** 401. `revoked` distinguishes a withdrawn session from never having had one. */
  | (FailureBase & { readonly kind: 'unauthenticated'; readonly revoked: boolean })
  /** 403 — signed in, not allowed. Often the wrong organisation is selected. */
  | (FailureBase & { readonly kind: 'forbidden' })
  | (FailureBase & { readonly kind: 'notFound' })
  /** 409 — the last writer lost, or the action conflicts with current state. */
  | (FailureBase & { readonly kind: 'conflict' })
  /** 410 — it existed and has expired. Signed artifact URLs return this. */
  | (FailureBase & { readonly kind: 'gone' })
  | (FailureBase & {
      readonly kind: 'validation';
      readonly fieldErrors: Readonly<Record<string, readonly string[]>>;
    })
  | (FailureBase & {
      readonly kind: 'rateLimited';
      readonly retryAfterSeconds: number | undefined;
    })
  | (FailureBase & { readonly kind: 'server' })
  | (FailureBase & { readonly kind: 'unknown' });

export type ApiFailureKind = ApiFailure['kind'];

/**
 * Where the transport folds `Retry-After` into the body.
 *
 * `ApiError` carries the status and the parsed body but not the headers, and
 * `packages/api-client` is generated — forking it to expose them would put
 * application behaviour in a package whose whole contract is that it holds
 * none. The transport normalises the one header the UI needs into the body it
 * does forward; this key is the agreement between the two.
 */
export const RETRY_AFTER_KEY = '__retry_after_seconds';

export function classifyApiError(error: unknown): ApiFailure {
  // Ordered before the ApiError check because an aborted fetch rejects with a
  // DOMException, not an ApiError — and a cancelled request that renders as
  // "something went wrong" is the most confusing error state there is, because
  // the user caused it deliberately by navigating away.
  if (isAbort(error)) {
    return { kind: 'cancelled', status: undefined, code: undefined, serverMessage: undefined };
  }

  if (error instanceof ApiError) return fromApiError(error);

  // fetch rejects with a TypeError for every transport-level failure: offline,
  // DNS, connection refused, and a CORS preflight the browser would not explain
  // to script anyway. They are one thing to the user — the API is unreachable.
  if (error instanceof TypeError) {
    return { kind: 'offline', status: undefined, code: undefined, serverMessage: undefined };
  }

  return {
    kind: 'unknown',
    status: undefined,
    code: undefined,
    serverMessage: error instanceof Error ? error.message : undefined,
  };
}

function fromApiError(error: ApiError): ApiFailure {
  const body = asRecord(error.body);
  const base: FailureBase = {
    status: error.status,
    code: readString(body, 'error'),
    serverMessage: readString(body, 'message'),
  };

  switch (error.status) {
    case 401:
      // P0-PLAT-07's EnsureSessionIsActive answers both "no session" and
      // "session withdrawn" with this code, so `revoked` is not a promise that
      // a session existed — it is what the API is willing to say.
      return { ...base, kind: 'unauthenticated', revoked: base.code === 'session_revoked' };
    case 403:
      return { ...base, kind: 'forbidden' };
    case 404:
      return { ...base, kind: 'notFound' };
    case 409:
      return { ...base, kind: 'conflict' };
    case 410:
      return { ...base, kind: 'gone' };
    case 422:
      return { ...base, kind: 'validation', fieldErrors: readFieldErrors(error) };
    case 429:
      return { ...base, kind: 'rateLimited', retryAfterSeconds: readRetryAfter(body) };
    default:
      if (error.status >= 500) return { ...base, kind: 'server' };
      return { ...base, kind: 'unknown' };
  }
}

/**
 * 422 field messages are the one place the server's own strings are rendered.
 *
 * They are also the only errors whose specificity cannot be reproduced here:
 * which field failed, and which of several rules on it. `packages/i18n`'s
 * `validation` namespace has generic keys for client-side checks, not a
 * rule-by-rule catalogue of Laravel's. The transport sends `Accept-Language` so
 * that these become localised the day apps/api negotiates a locale; until then
 * they are English, and that is a known gap rather than an oversight.
 */
function readFieldErrors(error: ApiError): Readonly<Record<string, readonly string[]>> {
  const errors = error.validationErrors;
  if (errors === undefined) return {};

  const cleaned: Record<string, readonly string[]> = {};
  for (const [field, messages] of Object.entries(errors)) {
    if (Array.isArray(messages) && messages.every((m) => typeof m === 'string')) {
      cleaned[field] = messages;
    }
  }
  return cleaned;
}

function readRetryAfter(body: Record<string, unknown> | undefined): number | undefined {
  const value = body?.[RETRY_AFTER_KEY];
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : undefined;
}

/**
 * An aborted fetch rejects with a `DOMException` named `AbortError`, and jsdom
 * and Node have historically differed on whether that is an instance of the
 * `DOMException` this realm has. The name is the part every implementation
 * agrees on.
 */
export function isAbort(error: unknown): boolean {
  return (
    typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError'
  );
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function readString(body: Record<string, unknown> | undefined, key: string): string | undefined {
  const value = body?.[key];
  return typeof value === 'string' && value !== '' ? value : undefined;
}
